/**
 * Converts a project ID to a Jira project ARI.
 *
 * @param projectId - The project ID (numeric string like "10001" or already an ARI)
 * @param cloudId - The cloud/tenant ID
 * @returns The full project ARI in format "ari:cloud:jira:{cloudId}:project/{projectId}"
 *
 * @example
 * ```typescript
 * convertProjectIdToAri("10001", "abc-123-def")
 * // returns: "ari:cloud:jira:abc-123-def:project/10001"
 *
 * convertProjectIdToAri("ari:cloud:jira:abc-123-def:project/10001", "abc-123-def")
 * // returns: "ari:cloud:jira:abc-123-def:project/10001" (unchanged)
 * ```
 */
export const convertProjectIdToAri = (projectId: string, cloudId: string): string => {
	// If already an ARI, return as-is
	if (projectId.startsWith('ari:')) {
		return projectId;
	}

	// Convert numeric project ID to ARI format
	return `ari:cloud:jira:${cloudId}:project/${projectId}`;
};

/**
 * Converts an array of project IDs to Jira project ARIs.
 *
 * @param projectIds - Array of project IDs (numeric strings or ARIs)
 * @param cloudId - The cloud/tenant ID
 * @returns Array of full project ARIs
 *
 * @example
 * ```typescript
 * convertProjectIdsToAris(["10001", "10002"], "abc-123-def")
 * // returns: [
 * //   "ari:cloud:jira:abc-123-def:project/10001",
 * //   "ari:cloud:jira:abc-123-def:project/10002"
 * // ]
 * ```
 */
export const convertProjectIdsToAris = (projectIds: string[], cloudId: string): string[] => {
	return projectIds.map((id) => convertProjectIdToAri(id, cloudId));
};
